import { type ComponentType, forwardRef, memo, useCallback, useMemo, useState } from 'react';

import { type Clip, ClipType } from '@air/api/types';
import {
  type Marker,
  PlayerPlayButton,
  PlayerPlaybackRateButton,
  PlayerPreview,
  PlayerPreviewContent,
  PlayerPreviewSpritesheet,
  PlayerPreviewTimestamp,
  PlayerPreviewTrigger,
  PlayerSeekbar,
  PlayerSeekbarChapters,
  PlayerSeekbarTimestamps,
  PlayerSeekbarTrack,
  PlayerSubtitlesButton,
  PlayerTimeButton,
  PlayerTimeFormatButton,
  PlayerVolumeButton,
  usePlayerStore,
} from '@air/component-player';
import { useVideoHlsPreview, VideoSeekbarPreviewHls } from '@air/component-video';
import { getVideoFps } from '@air/utils-video';

import { VideoSettingsButton } from '~/components/AssetModalV2/Content/Footer/components/VideoSettingsButton';
import { getCommentRangePercentStyle } from '~/components/v2/components/asset-modal/visualizer/video/getCommentRangePercentStyle';
import { useSeekbarChapters } from '~/components/v2/components/asset-modal/visualizer/video/hooks/useSeekbarChapters';
import { type Chapter } from '~/hooks/useAssetVersionChaptersList';

export type VideoVisualizerFullscreenControlsProps = {
  chapters?: Chapter[];
  clip: Clip;
  markers?: Marker[];
  SubtitlesButton?: ComponentType;
};

export const VideoVisualizerFullscreenControls = memo(
  forwardRef<HTMLDivElement, VideoVisualizerFullscreenControlsProps>(
    ({ chapters, clip, markers, SubtitlesButton = PlayerSubtitlesButton }, forwardedRef) => {
      const videoElement = usePlayerStore((state) => state.videoElement);
      const duration = usePlayerStore((state) => state.duration);
      const [hoveredMarker, setHoveredMarker] = useState<Marker | null>(null);
      const seekbarChapters = useSeekbarChapters(chapters);
      const { onUpdateVideo, videoRef } = useVideoHlsPreview({ clip });

      const fps = useMemo(() => {
        return getVideoFps(clip);
      }, [clip]);

      const isAudioClip = clip.type === ClipType.audio;

      const handleMarkerSelect = useCallback(
        (marker: Marker) => {
          if (videoElement) {
            videoElement.currentTime = marker.timestamp;
          }
        },
        [videoElement],
      );

      const handlePositionChange = useCallback(
        (position: number) => {
          void onUpdateVideo({
            duration: clip.duration ?? 0,
            position,
          });
        },
        [onUpdateVideo, clip.duration],
      );

      const seekbar = useMemo(() => {
        return (
          <PlayerSeekbar>
            {seekbarChapters?.length ? <PlayerSeekbarChapters chapters={seekbarChapters} /> : <PlayerSeekbarTrack />}
          </PlayerSeekbar>
        );
      }, [seekbarChapters]);

      return (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-20 z-portal flex items-center justify-center"
          data-theme="dark"
          ref={forwardedRef}
        >
          <div className="pointer-events-auto flex max-w-[824px] grow flex-col gap-2 rounded-8 bg-black/60 py-2 backdrop-blur">
            <div className="flex flex-col px-4">
              {markers !== undefined && markers.length > 0 ? (
                <div className="relative h-5">
                  <PlayerSeekbarTimestamps
                    markers={markers}
                    onMarkerHoverChange={setHoveredMarker}
                    onMarkerSelect={handleMarkerSelect}
                  />
                </div>
              ) : null}

              <div className="relative flex h-3 items-end justify-stretch">
                {isAudioClip ? (
                  seekbar
                ) : (
                  <PlayerPreview onPositionChange={handlePositionChange}>
                    <PlayerPreviewTrigger asChild>{seekbar}</PlayerPreviewTrigger>
                    <PlayerPreviewContent>
                      {clip.assets.spriteSheet ? (
                        <PlayerPreviewSpritesheet clip={clip} />
                      ) : (
                        // Backwards compatibility for old clips that haven't been processed to have a spritesheet.
                        <VideoSeekbarPreviewHls clip={clip} ref={videoRef} />
                      )}
                      <PlayerPreviewTimestamp />
                    </PlayerPreviewContent>
                  </PlayerPreview>
                )}

                {hoveredMarker?.endTimestamp !== undefined && duration > 0 ? (
                  <div
                    className="pointer-events-none absolute bottom-0 h-1 rounded-4 ads:bg-ads-gray-12 bg-gray-12"
                    style={getCommentRangePercentStyle(
                      { startTime: hoveredMarker.timestamp, endTime: hoveredMarker.endTimestamp },
                      duration,
                    )}
                  />
                ) : null}
              </div>
            </div>

            <div className="flex items-center px-2">
              <div className="flex flex-1 items-center justify-start gap-2">
                <PlayerPlayButton />
                <PlayerVolumeButton />
                <PlayerPlaybackRateButton />
              </div>
              <div className="flex items-center gap-2">
                <PlayerTimeButton fps={fps} seekDirection="backward" step={1} />
                <PlayerTimeFormatButton duration={clip.duration} frameCount={clip.frameCount} />
                <PlayerTimeButton fps={fps} seekDirection="forward" step={1} />
              </div>
              <div className="flex flex-1 items-center justify-end gap-2">
                <SubtitlesButton />
                <VideoSettingsButton />
              </div>
            </div>
          </div>
        </div>
      );
    },
  ),
);

VideoVisualizerFullscreenControls.displayName = 'VideoVisualizerFullscreenControls';
